import { useState } from 'react';
import { View, Text, Pressable, StyleSheet, ActivityIndicator, Modal, Platform } from 'react-native';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, ArrowRight, Calendar, Users, PenTool as Tool, Shield, Clock, Home, X } from '../lib/icons';
import { supabase } from '../lib/supabase';
import WebScrollView from '../components/WebScrollView';
import { shadows } from '../lib/styles';
import { useActiveHome } from '../contexts/ActiveHomeContext';
import { openExternalLink } from '../lib/platform';

const Plans = () => {
  const navigate = useNavigate();
  const { activeHome, refreshHomes } = useActiveHome();
  const [selectedPlan, setSelectedPlan] = useState<'free' | 'managed'>('free');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);

  const plans = [
    {
      id: 'free',
      name: 'Self-Managed',
      description: 'Perfect for homeowners who want to manage maintenance themselves',
      price: 'Free',
      features: [
        'Custom maintenance plan',
        'Maintenance tracking and reminders',
        'Service request management',
        'Expense tracking',
        'Unlimited homes',
        'Mobile-friendly dashboard'
      ]
    },
    {
      id: 'managed',
      name: 'Professionally Managed',
      description: 'Let us handle your home maintenance needs',
      price: 'Custom pricing based on your needs',
      priceNote: 'No payment required today',
      features: [
        'All Free features',
        'Dedicated home manager',
        'Professional maintenance service',
        'Regular inspections',
        'Priority support',
        '24/7 emergency assistance'
      ]
    }
  ];

  const handlePlanSelection = async () => {
    setLoading(true);
    setError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User not found');
      }

      if (selectedPlan === 'managed') {
        if (!activeHome?.id) {
          throw new Error('Add or select a home before upgrading to Premium');
        }

        const { data, error: checkoutError } = await supabase.functions.invoke(
          'create-billing-checkout-session',
          {
            body: {
              homeId: activeHome.id,
              slug: 'premium-plan',
            },
          },
        );

        if (checkoutError) throw checkoutError;
        const checkoutUrl = data?.url;
        if (!checkoutUrl) {
          throw new Error('Checkout session did not include a URL');
        }

        await openExternalLink(checkoutUrl);
        return;
      }

      if (!activeHome?.id) {
        throw new Error('Add or select a home before choosing a plan');
      }

      const { error: updateError } = await supabase
        .from('homes')
        .update({
          plan: 'free',
          plan_source: 'manual',
          plan_updated_at: new Date().toISOString(),
        })
        .eq('id', activeHome.id)
        .eq('owner_id', user.id);

      if (updateError) throw updateError;
      await refreshHomes();
      navigate('/');
    } catch (error: any) {
      console.error('Error saving plan selection:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getFeatureIcon = (feature: string) => {
    if (feature.includes('tracking')) return Tool;
    if (feature.includes('service')) return Users;
    if (feature.includes('maintenance')) return Calendar;
    if (feature.includes('emergency')) return Shield;
    if (feature.includes('24/7')) return Clock;
    return Home;
  };

  return (
    <View style={styles.container}>
      <WebScrollView 
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.header}>
          <Text style={styles.title}>Choose Your Plan</Text>
          <Text style={styles.subtitle}>
            Select the plan that best fits your needs. You can always upgrade or downgrade later.
          </Text>
        </View>

        <View style={styles.plansGrid}>
          {plans.map((plan) => {
            const isSelected = selectedPlan === plan.id;
            return (
              <View
                key={plan.id}
                style={[
                  styles.planCard,
                  isSelected && styles.planCardSelected
                ]}
              >
                <View style={styles.planContent}>
                  <View style={styles.planHeader}>
                    <View style={styles.planHeaderText}>
                      <Text style={styles.planName}>{plan.name}</Text>
                      <Text style={styles.planDescription}>{plan.description}</Text>
                    </View>
                    <Pressable
                      onPress={() => setSelectedPlan(plan.id as 'free' | 'managed')}
                      style={[
                        styles.radioButton,
                        isSelected && styles.radioButtonSelected
                      ]}
                    >
                      {isSelected && <CheckCircle2 size={16} color="#ffffff" />}
                    </Pressable>
                  </View>

                  <View style={styles.priceSection}>
                    <Text style={styles.price}>{plan.price}</Text>
                    {'priceNote' in plan && (
                      <Text style={styles.priceNote}>{plan.priceNote}</Text>
                    )}
                  </View>

                  <View style={styles.featuresList}>
                    {plan.features.map((feature, index) => {
                      const Icon = getFeatureIcon(feature);
                      return (
                        <View key={index} style={styles.featureItem}>
                          <View style={styles.featureIcon}>
                            <Icon size={16} color="#4b5563" />
                          </View>
                          <Text style={styles.featureText}>{feature}</Text>
                        </View>
                      );
                    })}
                  </View>
                </View>

                <View style={styles.planFooter}>
                  <Pressable
                    onPress={() => setSelectedPlan(plan.id as 'free' | 'managed')}
                    style={({ pressed }) => [
                      styles.selectButton,
                      isSelected ? styles.selectButtonSelected : styles.selectButtonUnselected,
                      pressed && styles.selectButtonPressed
                    ]}
                  >
                    <Text style={[
                      styles.selectButtonText,
                      isSelected ? styles.selectButtonTextSelected : styles.selectButtonTextUnselected
                    ]}>
                      {isSelected ? 'Selected' : 'Select'} {plan.name}
                    </Text>
                  </Pressable>
                </View>
              </View>
            );
          })}
        </View>

        {error && (
          <Text style={styles.errorText}>{error}</Text>
        )}

        <View style={styles.confirmSection}>
          <Pressable
            onPress={handlePlanSelection}
            disabled={loading}
            style={({ pressed }) => [
              styles.confirmButton,
              (loading || pressed) && styles.confirmButtonDisabled
            ]}
          >
            {loading ? (
              <View style={styles.buttonContent}>
                <ActivityIndicator size="small" color="#ffffff" />
                <Text style={styles.confirmButtonText}>Confirming...</Text>
              </View>
            ) : (
              <View style={styles.buttonContent}>
                <Text style={styles.confirmButtonText}>Confirm Selection</Text>
                <ArrowRight size={20} color="#ffffff" />
              </View>
            )}
          </Pressable>
        </View>
      </WebScrollView>

      {/* Confirmation Modal */}
      <Modal
        visible={showConfirmation}
        transparent
        animationType="fade"
        onRequestClose={() => navigate('/')}
      >
        <View style={styles.modalOverlay}>
          <Pressable style={styles.modalBackdrop} onPress={() => navigate('/')} />
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <View style={styles.modalIconContainer}>
                <CheckCircle2 size={24} color="#16a34a" />
              </View>
              <Pressable
                onPress={() => navigate('/')}
                style={styles.modalCloseButton}
              >
                <X size={20} color="#9ca3af" />
              </Pressable>
            </View>

            <Text style={styles.modalTitle}>Thank You!</Text>
            <View style={styles.modalBody}>
              <Text style={styles.modalText}>
                We're excited to help you maintain your home! Our team will contact you within 1-2 business days to:
              </Text>
              <View style={styles.modalList}>
                <View style={styles.modalListItem}>
                  <CheckCircle2 size={20} color="#16a34a" />
                  <Text style={styles.modalListText}>Discuss your home maintenance needs</Text>
                </View>
                <View style={styles.modalListItem}>
                  <CheckCircle2 size={20} color="#16a34a" />
                  <Text style={styles.modalListText}>Create a customized maintenance plan</Text>
                </View>
                <View style={styles.modalListItem}>
                  <CheckCircle2 size={20} color="#16a34a" />
                  <Text style={styles.modalListText}>Provide pricing based on your requirements</Text>
                </View>
              </View>
              <Text style={styles.modalText}>
                In the meantime, you can start using all the free features of HoneyDone to manage your home.
              </Text>
            </View>

            <Pressable
              onPress={() => navigate('/')}
              style={({ pressed }) => [
                styles.modalButton,
                pressed && styles.modalButtonPressed
              ]}
            >
              <View style={styles.buttonContent}>
                <Text style={styles.modalButtonText}>Go to Dashboard</Text>
                <ArrowRight size={20} color="#ffffff" />
              </View>
            </Pressable>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f9fafb',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    gap: 32,
    paddingBottom: 32,
  },
  header: {
    alignItems: 'center',
    gap: 16,
  },
  title: {
    fontSize: 30,
    fontWeight: 'bold',
    color: '#111827',
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 16,
    color: '#4b5563',
    textAlign: 'center',
    maxWidth: 600,
  },
  plansGrid: {
    gap: 24,
    ...(Platform.OS === 'web' && {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'center',
    }),
  },
  planCard: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    borderWidth: 2,
    borderColor: '#f3f4f6',
    overflow: 'hidden',
    ...shadows.sm,
    ...(Platform.OS === 'web' && {
      flex: 1,
      minWidth: 300,
      maxWidth: 500,
    }),
  },
  planCardSelected: {
    borderColor: '#000000',
    ...shadows.md,
  },
  planContent: {
    padding: 24,
    gap: 24,
  },
  planHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
  },
  planHeaderText: {
    flex: 1,
    gap: 4,
  },
  planName: {
    fontSize: 20,
    fontWeight: '600',
    color: '#111827',
  },
  planDescription: {
    fontSize: 14,
    color: '#6b7280',
    marginTop: 4,
  },
  radioButton: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: '#d1d5db',
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioButtonSelected: {
    backgroundColor: '#000000',
    borderColor: '#000000',
  },
  priceSection: {
    gap: 4,
  },
  price: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#111827',
  },
  priceNote: {
    fontSize: 14,
    color: '#6b7280',
  },
  featuresList: {
    gap: 12,
  },
  featureItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  featureIcon: {
    width: 32,
    height: 32,
    backgroundColor: '#f9fafb',
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  featureText: {
    fontSize: 14,
    color: '#4b5563',
    flex: 1,
  },
  planFooter: {
    borderTopWidth: 1,
    borderTopColor: '#f3f4f6',
    padding: 24,
  },
  selectButton: {
    width: '100%',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  selectButtonSelected: {
    backgroundColor: '#000000',
  },
  selectButtonUnselected: {
    backgroundColor: '#f9fafb',
  },
  selectButtonPressed: {
    opacity: 0.8,
  },
  selectButtonText: {
    fontSize: 16,
    fontWeight: '500',
  },
  selectButtonTextSelected: {
    color: '#ffffff',
  },
  selectButtonTextUnselected: {
    color: '#1f2937',
  },
  errorText: {
    color: '#ef4444',
    fontSize: 14,
    textAlign: 'center',
  },
  confirmSection: {
    alignItems: 'center',
  },
  confirmButton: {
    paddingVertical: 12,
    paddingHorizontal: 32,
    backgroundColor: '#000000',
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  confirmButtonDisabled: {
    opacity: 0.5,
  },
  buttonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  confirmButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '500',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },
  modalBackdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  modalContent: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 24,
    maxWidth: 500,
    width: '100%',
    ...shadows.xl,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 24,
  },
  modalIconContainer: {
    width: 48,
    height: 48,
    backgroundColor: '#f0fdf4',
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalCloseButton: {
    padding: 4,
  },
  modalTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 16,
  },
  modalBody: {
    gap: 16,
  },
  modalText: {
    fontSize: 14,
    color: '#4b5563',
    lineHeight: 20,
  },
  modalList: {
    gap: 8,
  },
  modalListItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  modalListText: {
    fontSize: 14,
    color: '#4b5563',
    flex: 1,
  },
  modalButton: {
    width: '100%',
    marginTop: 24,
    paddingVertical: 12,
    paddingHorizontal: 24,
    backgroundColor: '#000000',
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalButtonPressed: {
    opacity: 0.8,
  },
  modalButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '500',
  },
});

export default Plans;